import React, { useState } from 'react';
import { ChevronDown, Gavel, ScrollText, ShieldCheck, FileSearch } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

const STATE_BADGE = {
  esperando_jorge: { text: 'text-amber-500 dark:text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Esperando Jorge' },
  rechazada_tribunal: { text: 'text-rose-500 dark:text-rose-300', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'Rechazada por tribunal' },
  rechazada_comite: { text: 'text-rose-500 dark:text-rose-300', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'Rechazada por comité' },
  rechazada_riesgo: { text: 'text-rose-500 dark:text-rose-300', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'Rechazada por reglas R' },
  rechazada_jorge: { text: 'text-rose-500 dark:text-rose-300', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'Rechazada por Jorge' },
  aprobada_jorge: { text: 'text-emerald-500 dark:text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Aprobada' },
  cerrada: { text: 'text-emerald-500 dark:text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Cerrada' },
  shadow: { text: 'text-slate-500 dark:text-slate-300', bg: 'bg-slate-500/10', border: 'border-slate-500/30', label: 'Shadow sim' },
  propuesta: { text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border', label: 'Propuesta' },
};

const fmt = (n, d = 2) => (n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d));

export default function CandidateCard({ candidata, onChanged }) {
  const [expandedTribunal, setExpandedTribunal] = useState(false);
  const [expandedComite, setExpandedComite] = useState(false);
  const [expandedGate, setExpandedGate] = useState(false);
  const [busy, setBusy] = useState(null); // 'approve' | 'reject'
  const [error, setError] = useState(null);

  const p = candidata.propuesta;
  const t = candidata.tribunal;
  const c = candidata.comite;
  const v = candidata.validacion_pre_jorge;
  const badge = STATE_BADGE[candidata.estado] || STATE_BADGE.propuesta;
  const dist = Math.abs(p.entry - p.stop);
  const rr = dist > 0 ? Math.abs(p.objetivo - p.entry) / dist : 0;

  const canDecide = candidata.estado === 'esperando_jorge';

  const doAction = async (kind) => {
    setBusy(kind); setError(null);
    try {
      const url = `/candidates/${candidata.candidata_id}/${kind === 'approve' ? 'approve' : 'reject'}`;
      await api.post(url, {});
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'La operación falló');
    } finally {
      setBusy(null);
    }
  };

  return (
    <article
      className="panel p-4"
      data-testid={`candidate-${candidata.candidata_id}`}
    >
      {/* Header */}
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-num text-xs px-2 py-1 rounded border border-border bg-muted">{p.estrategia}</span>
          <span className="font-mono-num text-sm font-semibold">{p.par}</span>
          <span className="text-xs text-muted-foreground">· {p.proveedor_llm}/{p.modelo_llm}</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/auditoria/${candidata.candidata_id}`}
            data-testid="btn-audit"
            className="inline-flex items-center gap-1 text-[11px] rounded-md px-2 py-1 border border-border text-muted-foreground hover:text-[hsl(var(--brand-accent))] hover:border-[hsl(var(--brand-accent))] transition-colors"
          >
            <FileSearch size={12} /> Ver auditoría
          </Link>
          <span className={`text-[11px] px-2 py-1 rounded border ${badge.bg} ${badge.text} ${badge.border}`} data-testid="candidate-state">
            {badge.label}
          </span>
        </div>
      </header>

      {/* Numbers strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="panel-soft px-2 py-1.5">
          <div className="text-[11px] text-muted-foreground">Entrada</div>
          <div className="font-mono-num text-sm">{fmt(p.entry)}</div>
        </div>
        <div className="panel-soft px-2 py-1.5">
          <div className="text-[11px] text-muted-foreground">Stop</div>
          <div className="font-mono-num text-sm">{fmt(p.stop)}</div>
        </div>
        <div className="panel-soft px-2 py-1.5">
          <div className="text-[11px] text-muted-foreground">Objetivo</div>
          <div className="font-mono-num text-sm">{fmt(p.objetivo)}</div>
        </div>
        <div className="panel-soft px-2 py-1.5">
          <div className="text-[11px] text-muted-foreground">R:R</div>
          <div className="font-mono-num text-sm">{fmt(rr, 2)}</div>
        </div>
      </div>

      {/* Tesis */}
      <p className="text-sm mb-3 whitespace-pre-line">{p.tesis}</p>

      {/* Señales */}
      {p.señales?.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {p.señales.map((s, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground">{s}</span>
          ))}
        </div>
      )}

      {/* Validación riesgo */}
      {v && (
        <div className="panel-soft px-3 py-2 mb-3">
          <button className="w-full text-left flex items-center justify-between" onClick={() => setExpandedGate(!expandedGate)} data-testid="toggle-gate">
            <div className="flex items-center gap-2 text-xs">
              <ShieldCheck size={14} className={v.aprobada_por_codigo ? 'text-emerald-500' : 'text-rose-500'} />
              <span className="font-medium">Motor de riesgo (R1–R14)</span>
              <span className={v.aprobada_por_codigo ? 'text-emerald-500' : 'text-rose-500'}>
                {v.aprobada_por_codigo ? 'aprobado' : `bloqueado: ${(v.reglas_violadas || []).join(', ') || '—'}`}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>Sizing <span className="font-mono-num">{fmt(v.sizing_usdt)}</span> USDT</span>
              <span>Riesgo <span className="font-mono-num">{fmt(v.riesgo_usd)}</span> USDT</span>
              <ChevronDown size={14} className={`transition-transform ${expandedGate ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {expandedGate && (
            <div className="pt-2 mt-2 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div>Qty: <span className="font-mono-num">{fmt(v.qty, 6)}</span></div>
              <div>Advertencias: {v.reglas_advertencia?.join(', ') || 'ninguna'}</div>
            </div>
          )}
        </div>
      )}

      {/* Tribunal */}
      {t && (
        <div className="panel-soft px-3 py-2 mb-3">
          <button className="w-full text-left flex items-center justify-between" onClick={() => setExpandedTribunal(!expandedTribunal)} data-testid="toggle-tribunal">
            <div className="flex items-center gap-2 text-xs">
              <Gavel size={14} className={t.aprobado_tribunal ? 'text-emerald-500' : 'text-rose-500'} />
              <span className="font-medium">Tribunal ({t.actas?.length || 0} fiscales)</span>
              <span className={t.aprobado_tribunal ? 'text-emerald-500' : 'text-rose-500'}>
                {t.aprobado_tribunal ? 'sin vetos' : `${t.votos_veto} veto${t.votos_veto === 1 ? '' : 's'}`}
              </span>
              <span className="text-muted-foreground">· oposición prom. <span className="font-mono-num">{fmt(t.oposicion_promedio, 2)}</span></span>
            </div>
            <ChevronDown size={14} className={`transition-transform ${expandedTribunal ? 'rotate-180' : ''}`} />
          </button>
          {expandedTribunal && (
            <div className="pt-2 mt-2 border-t border-border space-y-2">
              {t.actas.map((a, i) => (
                <div key={i} className="text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{a.fiscal}</span>
                    <span className="text-muted-foreground">{a.proveedor_llm}/{a.modelo_llm}</span>
                    <span className={a.veto ? 'text-rose-500' : 'text-muted-foreground'}>· {a.veto ? 'VETO' : 'sin veto'}</span>
                    <span className="text-muted-foreground">· fuerza <span className="font-mono-num">{fmt(a.fuerza_oposicion, 2)}</span></span>
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {a.objeciones.map((o, j) => <li key={j}>{o}</li>)}
                  </ul>
                  {a.resumen_veredicto && <p className="text-muted-foreground mt-1 italic">“{a.resumen_veredicto}”</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comité */}
      {c && (
        <div className="panel-soft px-3 py-2 mb-3">
          <button className="w-full text-left flex items-center justify-between" onClick={() => setExpandedComite(!expandedComite)} data-testid="toggle-comite">
            <div className="flex items-center gap-2 text-xs">
              <ScrollText size={14} className={c.veredicto === 'presentar_a_jorge' ? 'text-emerald-500' : 'text-rose-500'} />
              <span className="font-medium">Comité</span>
              <span className={c.veredicto === 'presentar_a_jorge' ? 'text-emerald-500' : 'text-rose-500'}>{c.veredicto === 'presentar_a_jorge' ? 'presentar a Jorge' : 'descartar'}</span>
              <span className="text-muted-foreground">· riesgo {c.riesgo_operacional}</span>
            </div>
            <ChevronDown size={14} className={`transition-transform ${expandedComite ? 'rotate-180' : ''}`} />
          </button>
          {expandedComite && (
            <div className="pt-2 mt-2 border-t border-border text-xs space-y-2">
              <p>{c.razonamiento}</p>
              {(c.fortalezas?.length > 0) && (
                <div><span className="text-muted-foreground">Fortalezas: </span>{c.fortalezas.join('; ')}</div>
              )}
              {(c.debilidades?.length > 0) && (
                <div><span className="text-muted-foreground">Debilidades: </span>{c.debilidades.join('; ')}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cierre / post-mortem si ya cerró */}
      {candidata.cierre && (
        <div className="panel-soft px-3 py-2 mb-3 text-xs">
          <div className="flex items-center gap-3 flex-wrap">
            <span>Cierre: <span className="font-medium">{candidata.cierre.motivo}</span></span>
            <span>R: <span className={`font-mono-num ${candidata.cierre.r_multiplo > 0 ? 'pnl-pos' : candidata.cierre.r_multiplo < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(candidata.cierre.r_multiplo, 2)}</span></span>
            <span>PnL: <span className={`font-mono-num ${candidata.cierre.pnl_usdt > 0 ? 'pnl-pos' : candidata.cierre.pnl_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(candidata.cierre.pnl_usdt, 2)} USDT</span></span>
            <span>Salida: <span className="font-mono-num">{fmt(candidata.cierre.precio_salida)}</span></span>
          </div>
          {candidata.post_mortem?.resumen && <p className="text-muted-foreground mt-1">{candidata.post_mortem.resumen}</p>}
        </div>
      )}

      {/* Advertencias */}
      {candidata.advertencias?.length > 0 && (
        <div className="text-[11px] text-amber-500/90 mb-3">
          ⚠ {candidata.advertencias.join(' · ')}
        </div>
      )}

      {error && <div className="text-sm text-destructive mb-3" data-testid="candidate-error">{error}</div>}

      {/* Botones de decisión */}
      {canDecide && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button
            data-testid="btn-approve"
            disabled={!!busy}
            onClick={() => doAction('approve')}
            className="flex-1 px-3 py-2 rounded-md text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
          >{busy === 'approve' ? 'Aprobando...' : 'Aprobar'}</button>
          <button
            data-testid="btn-reject"
            disabled={!!busy}
            onClick={() => doAction('reject')}
            className="flex-1 px-3 py-2 rounded-md text-sm border border-rose-500/40 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 disabled:opacity-60"
          >{busy === 'reject' ? 'Rechazando...' : 'Rechazar'}</button>
        </div>
      )}
      {!canDecide && candidata.propuesta.estrategia === 'S5-B' && candidata.estado !== 'shadow' && candidata.estado !== 'aprobada_jorge' && candidata.estado !== 'cerrada' && (
        <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">Al rechazar candidatas S5-B se dispara automáticamente una <strong>simulación en sombra</strong> con slippage 0,30% por lado.</div>
      )}
    </article>
  );
}
